/**
 * Module dependencies.
 */

import {
  Button,
  Checkbox,
  HelpText,
  Input,
  Password,
  Select
} from 'src/components/core';

import { FormData } from 'src/api/user/create-user';
import { ReactElement, useCallback } from 'react';
import { regexes } from 'src/core/utils/regexes';
import { units } from '@untile/react-components';
import { useCountriesList } from 'src/hooks/use-countries-list';
import { useForm } from 'react-hook-form';
import TermsConditionsCheckboxLabel from '../core/forms/terms-conditions-checkbox-label';
import map from 'lodash/map';
import omit from 'lodash/omit';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  isSubmitting: boolean;
  onSubmit: (formData: FormData) => void;
};

/**
 * `InfoData` type.
 */

type InfoData = {
  acceptTerms: boolean;
  company: string;
  country: string;
  email: string;
  name: string;
  password: string;
  phone?: string | null;
  subscribeToNewsletter?: boolean;
};

/**
 * `StyledInput` styled component.
 */

const StyledInput = styled(Input)`
  margin-bottom: ${units(3.5)};
`;

/**
 * `StyledSelect` styled component.
 */

const StyledSelect = styled(Select)`
  margin-bottom: ${units(3.5)};
`;

/**
 * `StyledPassword` styled component.
 */

const StyledPassword = styled(Password)`
  margin-bottom: ${units(4.5)};
`;

/**
 * `StyledCheckbox` styled component.
 */

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: ${units(5)};
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  max-width: ${units(58)};
  width: 100%;
`;

/**
 * `SignUpForm` component.
 */

const SignUpForm = (props: Props): ReactElement => {
  const { className, isSubmitting, onSubmit } = props;
  const { translate } = useTranslate();
  const {
    formState: { errors },
    handleSubmit,
    register
  } = useForm({
    mode: 'onBlur'
  });

  const countriesList = useCountriesList();
  const handleSubmitForm = useCallback(
    (formData: InfoData) => {
      const { email, password, ...rest } = formData;
      const normalizedFormData = {
        email,
        info: { ...omit(rest, ['acceptTerms']) },
        password
      };

      onSubmit(normalizedFormData);
    },
    [onSubmit]
  );

  return (
    <form
      className={className}
      onSubmit={handleSubmit(handleSubmitForm)}
    >
      <StyledInput
        disabled={isSubmitting}
        error={errors?.company}
        isRequired
        label={translate('forms:labels.company')}
        name={'company'}
        placeholder={translate('forms:placeholders.company')}
        {...register('company', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <StyledInput
        disabled={isSubmitting}
        error={errors?.name}
        isRequired
        label={translate('forms:labels.name')}
        name={'name'}
        placeholder={translate('forms:placeholders.name')}
        {...register('name', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <StyledInput
        disabled={isSubmitting}
        error={errors?.email}
        isRequired
        label={translate('forms:labels.email')}
        name={'email'}
        placeholder={translate('forms:placeholders.email')}
        {...register('email', {
          pattern: {
            message: translate('forms:rules.email'),
            value: regexes.email
          },
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <StyledInput
        disabled={isSubmitting}
        error={errors?.phone}
        label={translate('forms:labels.phone')}
        name={'phone'}
        placeholder={translate('forms:placeholders.phone')}
        {...register('phone')}
      />

      <StyledSelect
        disabled={isSubmitting}
        error={errors?.country}
        isRequired
        label={translate('forms:labels.country')}
        name={'country'}
        {...register('country', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      >
        <>
          <option value={''}>{translate('forms:placeholders.select')}</option>

          {map(countriesList, ({ alpha2, name }) => (
            <option
              key={alpha2}
              value={name}
            >
              {name}
            </option>
          ))}
        </>
      </StyledSelect>

      <StyledPassword
        disabled={isSubmitting}
        error={errors?.password}
        helpText={translate('forms:helpTexts.password')}
        label={translate('forms:labels.password')}
        name={'password'}
        placeholder={translate('forms:placeholders.password')}
        {...register('password', {
          pattern: {
            message: translate('forms:rules.password'),
            value: regexes.password
          },
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <Checkbox
        disabled={isSubmitting}
        label={
          <TermsConditionsCheckboxLabel
            i18nKey={'forms:labels.termsConditions'}
          />
        }
        name={'acceptTerms'}
        {...register('acceptTerms', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      {errors.acceptTerms && (
        <HelpText hasError>
          {translate('forms:rules.acceptTermsConditions')}
        </HelpText>
      )}

      <StyledCheckbox
        disabled={isSubmitting}
        id={'subscribeToNewsletter'}
        label={translate('forms:labels.allowMarketing')}
        name={'subscribeToNewsletter'}
        {...register('subscribeToNewsletter')}
      />

      <StyledButton
        isLoading={isSubmitting}
        type={'submit'}
      >
        {translate('common:actions.signUp')}
      </StyledButton>
    </form>
  );
};

/**
 * Export `SignUpForm` component.
 */

export default SignUpForm;
